// Pure grouping and formatting for the Admin settings model pickers
// (ANTHROPIC_API_PROVIDER_PLAN Task 7). Prices come from the shared catalog so
// the hand-written cost notes that used to drift are gone.

import {
  AI_MODELS,
  ALLOWED_AI_MODEL_IDS,
  estimateProfileUsd,
  providerLabel,
  type AiProvider,
  type AllowedAiModelId,
} from "@checklist/shared";

export type AiModelSurface = "generation" | "chatConversation";

export interface AiModelOption {
  id: AllowedAiModelId;
  label: string;
  /** Muted line: list price plus the per-use estimate. */
  note: string;
}

export interface AiModelGroup {
  provider: AiProvider;
  heading: string;
  options: AiModelOption[];
}

const PROVIDER_ORDER: readonly AiProvider[] = ["bedrock", "anthropic"];

/** Estimated USD, 2 significant figures; below $0.001 it is a floor, not a number. */
export function formatEstimateUsd(usd: number): string {
  if (usd < 0.001) return "<$0.001";
  return `$${Number(usd.toPrecision(2))}`;
}

/** Per-MTok list price, without trailing zeros: 0.1 -> "0.1", 3 -> "3". */
function formatRate(rate: number): string {
  return String(Number(rate.toPrecision(3)));
}

export function modelNote(id: AllowedAiModelId, surface: AiModelSurface): string {
  const { rates } = AI_MODELS[id];
  const per = surface === "generation" ? "per list" : "per 10-turn chat";
  return `$${formatRate(rates.input)}/$${formatRate(rates.output)} per MTok · ~${formatEstimateUsd(
    estimateProfileUsd(id, surface)
  )} ${per}`;
}

/** Every allowlisted id exactly once, grouped by provider (Bedrock first). */
export function aiModelGroups(surface: AiModelSurface): AiModelGroup[] {
  return PROVIDER_ORDER.map((provider) => ({
    provider,
    heading: providerLabel(provider),
    options: ALLOWED_AI_MODEL_IDS.filter((id) => AI_MODELS[id].provider === provider).map((id) => ({
      id,
      label: AI_MODELS[id].label,
      note: modelNote(id, surface),
    })),
  }));
}
